/**
 * Filter extraction utilities for task overview and other filterable components
 * 
 * These utilities help extract and transform filter values from SearchParams
 * into the format expected by backend APIs.
 */

/**
 * Creates a configurable filter extractor function
 * 
 * @param {Object} options Configuration options
 * @param {string} options.prefix - Prefix to match filter keys (e.g., 'subtask_')
 * @param {string[]} [options.excludePrefixes] - Prefixes to exclude from results
 * @param {'array'|'single'|'identity'} [options.valueTransform] - How to transform the value
 * @returns {Function} Extractor function that takes a filter object
 */
export const createFilterExtractor = (options) => (filter) => {
  if (!filter || typeof filter !== 'object') {
    return {};
  }

  return Object.keys(filter)
    .filter(key => {
      // Must start with the specified prefix
      if (!key.startsWith(options.prefix)) {
        return false;
      }
      
      // Exclude keys that start with any excluded prefix
      if (options.excludePrefixes && options.excludePrefixes.length > 0) {
        return !options.excludePrefixes.some(excludePrefix => key.startsWith(excludePrefix));
      }
      
      return true;
    })
    .reduce((result, key) => {
      const filterValue = filter[key];
      const items = filterValue?.items;
      
      // Skip if no items
      if (!items) {
        return result;
      }
      
      let transformedValue = items;
      
      // Transform value based on configuration
      if (options.valueTransform === 'single') {
        // Extract single value (first item) - for user filters
        if (items.length > 0) {
          transformedValue = items[0];
        } else {
          // Skip empty single values
          return result;
        }
      } else if (options.valueTransform === 'identity') {
        // Return the value as-is
        transformedValue = items;
      }
      // default: 'array' - keep as array
      
      // Extract the key by removing the prefix
      const extractedKey = key.substring(options.prefix.length);
      
      return {
        ...result,
        [extractedKey]: transformedValue
      };
    }, {});
};

/**
 * Pre-configured extractor for subtask status filters
 * Extracts filters like 'subtask_<guid>' but excludes 'subtask_user_<guid>'
 * Returns array values for status arrays
 */
export const extractSubtaskStatusFilters = createFilterExtractor({
  prefix: 'subtask_',
  excludePrefixes: ['subtask_user_'],
  valueTransform: 'array'
});

/**
 * Pre-configured extractor for subtask user filters
 * Extracts filters like 'subtask_user_<guid>'
 * Returns single GUID value (not array) since backend expects Dictionary<Guid, Guid>
 */
export const extractSubtaskUserFilters = createFilterExtractor({
  prefix: 'subtask_user_',
  valueTransform: 'single'
});

/**
 * Pre-configured extractor for parameter filters
 * Extracts filters like 'parameter_<guid>'
 * Returns array values
 */
export const extractParameterFilters = createFilterExtractor({
  prefix: 'parameter_',
  valueTransform: 'array'
});

/**
 * Generic extractor that keeps array values
 * Useful for custom filter scenarios
 */
export const extractFiltersWithPrefix = (filter, prefix) => {
  return createFilterExtractor({
    prefix,
    valueTransform: 'array'
  })(filter);
};
